import React from 'react';

const ServiceStatusCard = ({ data, P }) => {
  if (!data) return null;

  const summary = data.summary || {};
  const checkouts = data.checkouts_today || [];
  const checkins = data.checkins_today || [];
  const unassigned = data.unassigned_cleanings || [];
  const reschedules = data.pending_reschedules || [];

  // Status badge logic
  const isRed = unassigned.length > 2 || reschedules.length > 3;
  const isYellow = !isRed && (unassigned.length > 0 || reschedules.length > 0);
  const statusColor = isRed ? '#ef4444' : isYellow ? '#eab308' : '#22c55e';
  const statusLabel = isRed ? 'Critical' : isYellow ? 'Needs Attention' : 'All Clear';

  // Detect same-day turnovers (checkout + checkin on same property)
  const checkinProps = new Set(checkins.map(c => c.property));

  const hasOps = checkouts.length > 0 || checkins.length > 0;

  return (
    <div style={{
      borderRadius: '10px', border: `1px solid ${P.border}`,
      overflow: 'hidden', marginBottom: '12px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderBottom: `1px solid ${P.border}`,
        backgroundColor: P.tableHeader,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: P.textBright }}>
            Operations — {data.date || 'Today'}
          </span>
        </div>
        <span style={{
          fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px',
          backgroundColor: statusColor + '18', color: statusColor, letterSpacing: '0.3px',
        }}>
          {statusLabel}
        </span>
      </div>

      {/* Table */}
      {hasOps ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['Property', 'Checkout', 'Check-in', 'Aynista', 'Status'].map(h => (
                  <th key={h} style={{
                    padding: '7px 12px', textAlign: 'left', fontSize: '10px',
                    fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
                    color: P.textMuted, borderBottom: `1px solid ${P.border}`,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {checkouts.map((co, i) => {
                const isTurnover = checkinProps.has(co.property);
                const ci = isTurnover ? checkins.find(c => c.property === co.property) : null;
                return (
                  <tr key={i} style={{
                    backgroundColor: isTurnover ? (statusColor === '#ef4444' ? 'rgba(239,68,68,0.04)' : 'rgba(234,179,8,0.04)') : 'transparent',
                  }}>
                    <td style={{ padding: '7px 12px', color: P.textBright, fontWeight: 500, borderBottom: `1px solid ${P.border}` }}>
                      {co.property}
                    </td>
                    <td style={{ padding: '7px 12px', color: P.text, borderBottom: `1px solid ${P.border}` }}>
                      {co.checkout_time}
                    </td>
                    <td style={{ padding: '7px 12px', color: P.text, borderBottom: `1px solid ${P.border}` }}>
                      {ci ? ci.checkin_time : '—'}
                    </td>
                    <td style={{ padding: '7px 12px', color: P.text, borderBottom: `1px solid ${P.border}` }}>
                      {co.assigned_cleaner || '—'}
                    </td>
                    <td style={{ padding: '7px 12px', borderBottom: `1px solid ${P.border}` }}>
                      <span style={{
                        fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
                        backgroundColor: co.status === 'assigned' ? '#22c55e18' : '#ef444418',
                        color: co.status === 'assigned' ? '#22c55e' : '#ef4444',
                      }}>
                        {co.status === 'assigned' ? 'Assigned' : 'Unassigned'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {/* Check-ins without a matching checkout */}
              {checkins.filter(ci => !checkouts.some(co => co.property === ci.property)).map((ci, i) => (
                <tr key={`ci-${i}`}>
                  <td style={{ padding: '7px 12px', color: P.textBright, fontWeight: 500, borderBottom: `1px solid ${P.border}` }}>
                    {ci.property}
                  </td>
                  <td style={{ padding: '7px 12px', color: P.textMuted, borderBottom: `1px solid ${P.border}` }}>—</td>
                  <td style={{ padding: '7px 12px', color: P.text, borderBottom: `1px solid ${P.border}` }}>
                    {ci.checkin_time}
                  </td>
                  <td style={{ padding: '7px 12px', color: P.textMuted, borderBottom: `1px solid ${P.border}` }}>—</td>
                  <td style={{ padding: '7px 12px', borderBottom: `1px solid ${P.border}` }}>
                    <span style={{
                      fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '10px',
                      backgroundColor: P.tableHeader, color: P.textMuted,
                    }}>Check-in only</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: '20px 14px', textAlign: 'center', color: P.textMuted, fontSize: '13px' }}>
          No operations scheduled today
        </div>
      )}

      {/* Summary row */}
      <div style={{
        display: 'flex', gap: '16px', padding: '8px 14px',
        borderTop: `1px solid ${P.border}`, fontSize: '11px', color: P.textMuted,
      }}>
        <span>{summary.total_checkouts || 0} checkouts</span>
        <span>{summary.total_checkins || 0} check-ins</span>
        <span>{summary.total_properties || 0} properties</span>
      </div>

      {/* Risk callout */}
      {(unassigned.length > 0 || reschedules.length > 0) && (
        <div style={{
          margin: '0 10px 10px', padding: '8px 12px', borderRadius: '8px',
          backgroundColor: isRed ? 'rgba(239,68,68,0.06)' : 'rgba(234,179,8,0.06)',
          border: `1px solid ${isRed ? 'rgba(239,68,68,0.15)' : 'rgba(234,179,8,0.15)'}`,
          fontSize: '12px', color: isRed ? '#ef4444' : '#b45309',
        }}>
          {unassigned.length > 0 && (
            <div style={{ marginBottom: reschedules.length > 0 ? '4px' : 0 }}>
              {unassigned.length} unassigned cleaning{unassigned.length > 1 ? 's' : ''} need coverage
            </div>
          )}
          {reschedules.length > 0 && (
            <div>{reschedules.length} pending reschedule request{reschedules.length > 1 ? 's' : ''}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceStatusCard;
